import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Link from 'next/link';
import {
  Wallet,
  Clock,
  Heart,
  Gift,
  TrendingUp,
  Users,
  ArrowRight,
} from 'lucide-react';
import { IconCard } from '../IconCard';
import { CAREERS_COPY } from '../../../constants/careersTranslations';

const BENEFIT_ICON_MAP = { Wallet, Clock, Heart, Gift, TrendingUp, Users };

/** Strip literal </br>/<br> that may come from WordPress so they are not shown as text. */
function stripBrText(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/<\/?br\s*\/?>/gi, '').trim();
}

/** Split by <br />, <br>, </br> and render as text + <br /> (no raw HTML). */
function renderItemWithLineBreaks(item) {
  if (typeof item !== 'string') return item;
  const cleaned = stripBrText(item);
  const parts = cleaned.split(/<br\s*\/?>|<\/br>/gi).filter((p) => p !== '');
  if (parts.length <= 1) return cleaned;
  return parts.flatMap((part, i) => (i === parts.length - 1 ? [part] : [part, <br key={`br-${i}`} />]));
}

export function CareersBenefitsSection({ lang = 'en', copy }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const t = copy?.benefits ?? CAREERS_COPY[lang]?.benefits ?? CAREERS_COPY.en.benefits;
  const groups = t.benefitGroups ?? [];

  return (
    <section ref={ref} className="section-rhythm bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="heading-2 mb-12 text-gray-900 text-center"
        >
          {t.title}
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group, index) => {
            const Icon = BENEFIT_ICON_MAP[group.icon];
            return (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.06 }}
                className="group p-6 rounded-2xl border border-gray-200 bg-white hover:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)] transition-all"
              >
                {Icon && <IconCard Icon={Icon} className="mb-4" />}
                <h3 className="font-bold text-gray-900 mb-3">{group.title}</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  {(group.items ?? []).map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-brand shrink-0">•</span>
                      <span>{renderItemWithLineBreaks(item)}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
        {t.linkHref && t.linkText && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-10"
          >
            <Link
              href={t.linkHref}
              className="inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all"
            >
              {t.linkText}
              <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}
